import type {
    CredentialBinding,
    LocalCredentialRecord,
    WrappedKeyRecord
} from '@tadpoles/local-encryption'
import { Type, type StaticDecode } from 'typebox'

import type { LocalEncryptionStore } from '@tadpoles/local-encryption'
import type {
    MappedPersistenceCollectionDefinition,
    PersistenceCollection
} from '@tadpoles/persistence'

const LocalEncryptionEnrollmentStorageRecord = Type.Object({
    key: Type.String({ minLength: 1 }),
    bindingKind: Type.Union([Type.Literal('local-device'), Type.Literal('server-passkey')]),
    userId: Type.String({ minLength: 1 }),
    deviceId: Type.Optional(Type.String({ minLength: 1 })),
    credentialCreatedAt: Type.Integer({ minimum: 0 }),
    credentialId: Type.String({ minLength: 1 }),
    keyVersion: Type.Integer({ minimum: 1 }),
    wrapAlgorithm: Type.Literal('AES-GCM'),
    wrappedKeyCreatedAt: Type.Integer({ minimum: 0 }),
    wrapNonce: Type.String({ minLength: 1 }),
    wrappedKey: Type.String({ minLength: 1 })
})

export type LocalEncryptionEnrollmentRecord = {
    credentialRecord: LocalCredentialRecord
    wrappedKeyRecord: WrappedKeyRecord
}
export type FamilyLocalEncryptionStore = LocalEncryptionStore
type LocalEncryptionEnrollmentCollection = PersistenceCollection<
    LocalEncryptionEnrollmentRecord,
    'key'
>

function serializeLocalEncryptionBinding(binding: CredentialBinding): string {
    switch (binding.kind) {
        case 'local-device':
            return `${binding.kind}:${binding.userId}:${binding.deviceId}`
        case 'server-passkey':
            return `${binding.kind}:${binding.userId}`
    }
}

function decodeLocalEncryptionBinding(
    value: StaticDecode<typeof LocalEncryptionEnrollmentStorageRecord>
): CredentialBinding {
    switch (value.bindingKind) {
        case 'local-device':
            if (value.deviceId === undefined) {
                throw new Error('Local encryption enrollment record is missing a device id.')
            }

            return {
                kind: 'local-device',
                userId: value.userId,
                deviceId: value.deviceId
            }
        case 'server-passkey':
            return {
                kind: 'server-passkey',
                userId: value.userId
            }
    }
}

export const LocalEncryptionEnrollmentCollectionDefinition: MappedPersistenceCollectionDefinition<
    typeof LocalEncryptionEnrollmentStorageRecord,
    LocalEncryptionEnrollmentRecord,
    'key'
> = {
    kind: 'local-encryption-enrollment',
    scope: 'user',
    primaryKey: 'key',
    modelSchema: LocalEncryptionEnrollmentStorageRecord,
    encode: (value) => {
        const binding = value.credentialRecord.binding

        return {
            key: serializeLocalEncryptionBinding(binding),
            bindingKind: binding.kind,
            userId: binding.userId,
            deviceId: binding.kind === 'local-device' ? binding.deviceId : undefined,
            credentialCreatedAt: value.credentialRecord.createdAt,
            credentialId: value.credentialRecord.credentialId,
            keyVersion: value.wrappedKeyRecord.keyVersion,
            wrapAlgorithm: value.wrappedKeyRecord.wrapAlgorithm,
            wrappedKeyCreatedAt: value.wrappedKeyRecord.createdAt,
            wrapNonce: value.wrappedKeyRecord.wrapNonce,
            wrappedKey: value.wrappedKeyRecord.wrappedKey
        }
    },
    decode: (value) => {
        const binding = decodeLocalEncryptionBinding(value)

        return {
            credentialRecord: {
                binding,
                createdAt: value.credentialCreatedAt,
                credentialId: value.credentialId
            },
            wrappedKeyRecord: {
                binding,
                createdAt: value.wrappedKeyCreatedAt,
                keyVersion: value.keyVersion,
                wrapAlgorithm: value.wrapAlgorithm,
                wrapNonce: value.wrapNonce,
                wrappedKey: value.wrappedKey
            }
        }
    }
}

export class FamilyLocalEncryptionStoreAdapter implements FamilyLocalEncryptionStore {
    readonly #collection: LocalEncryptionEnrollmentCollection

    public constructor(collection: LocalEncryptionEnrollmentCollection) {
        this.#collection = collection
    }

    public async getCredential(
        binding: CredentialBinding
    ): Promise<LocalCredentialRecord | null> {
        return this.#toCredentialRecord(
            await this.#collection.get(serializeLocalEncryptionBinding(binding))
        )
    }

    public async getWrappedKey(
        binding: CredentialBinding
    ): Promise<WrappedKeyRecord | null> {
        return this.#toWrappedKeyRecord(
            await this.#collection.get(serializeLocalEncryptionBinding(binding))
        )
    }

    public async putEnrollmentIfAbsent(records: {
        credentialRecord: LocalCredentialRecord
        wrappedKeyRecord: WrappedKeyRecord
    }): Promise<boolean> {
        const key = serializeLocalEncryptionBinding(records.credentialRecord.binding)

        return await this.#runWithBindingLock(key, async () => {
            const existing = await this.#collection.get(key)

            if (existing !== null) {
                return false
            }

            await this.#collection.put({
                credentialRecord: records.credentialRecord,
                wrappedKeyRecord: records.wrappedKeyRecord
            })

            return true
        })
    }

    #toCredentialRecord(
        enrollment: LocalEncryptionEnrollmentRecord | null
    ): LocalCredentialRecord | null {
        return enrollment?.credentialRecord ?? null
    }

    #toWrappedKeyRecord(
        enrollment: LocalEncryptionEnrollmentRecord | null
    ): WrappedKeyRecord | null {
        return enrollment?.wrappedKeyRecord ?? null
    }

    async #runWithBindingLock<TResult>(
        bindingKey: string,
        operation: () => Promise<TResult>
    ): Promise<TResult> {
        if (typeof window === 'undefined') {
            return await operation()
        }

        if (navigator.locks === undefined) {
            throw new Error(
                'Local encryption enrollment requires Web Locks support in this browser.'
            )
        }

        const lockName = `family-local-encryption:${bindingKey}`
        return await navigator.locks.request(lockName, async () => await operation())
    }
}
